import { useEffect, useRef } from 'react';

import type { Conversation, Message, Participant } from '@twilio/conversations';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import { conversationIsEnded } from '../../common/utils/conversation-status';
import { useGlobalConfig } from '../../common/utils/global-config';
import { useHasUnreadMessages } from '../../common/utils/use-has-unread-messages';
import { useWidgetVisibility } from '../../common/utils/widget-visibility';
import { useChatStore, useChatStoreActions } from '../../controllers/chat-store/main';
import type { TwilioLiveChatMessage } from '../../controllers/chat-store/twilio-live-chat';
import type { OmniEnhancedConversation } from '../../controllers/chat-store/types';

import { consumeRecentCsmClientMessageId } from './csm-client-message-id';
import { isWhatsAppAuthor } from './is-whatsapp-author';
import { fetchLiveChatMessageHistory } from './ocg-client-utils';
import { type OcgClient, useOcgClient } from './use-ocg-client';
import { useTwilioOcgClient } from './use-twilio-ocg-client';

// Module-level set to track which conversations have been backfilled.
// Survives component remounts (unlike useRef which resets).
const backfilledConversations = new Set<string>();
// Tracks the last conversation ID to detect actual conversation switches
let lastBackfilledConversationId: string | null = null;
// Module-level set to track conversations whose Twilio restore has failed.
// Prevents the LazyRestore useEffect from retrying endlessly when
// getTwilioConversation returns "Not Found" for ended/deleted conversations.
const failedRestoreConversations = new Set<string>();
// Exported for testing only
export const resetBackfilledConversations = (): void => {
	backfilledConversations.clear();
	lastBackfilledConversationId = null;
	failedRestoreConversations.clear();
};

const isParticipantAnAgent = (participant: Participant, helpSeekerIdentity: string | null) =>
	!!participant.identity && participant.identity !== helpSeekerIdentity;

export const TwilioLiveChatManager = () => {
	di(useChatStore, useChatStoreActions, useTwilioOcgClient, useOcgClient);

	const { onHasUnreadMessagesChanged } = useGlobalConfig();
	const ocgClient = useOcgClient();
	const [
		{
			currentConversationId,
			twilioClientIdentity,
			twilioConversationsWithMetadata,
			conversations,
			twilioBootstrapFatalError,
		},
	] = useChatStore();
	const {
		addTwilioLiveChatHumanMessage,
		addTwilioLiveChatMessage,
		backfillLiveChatMessages,
		initializeTwilioConversation,
		setCsmLiveChatSubscriptionId,
		addTwilioAgentTyping,
		removeTwilioAgentTyping,
		clearTwilioAgentsTyping,
		setTwilioAgentJoined,
		hydrateTwilioConversation,
		updateTwilioUnreadMessagesCount,
		checkInitialUnreadMessages,
		markTwilioConversationEnded,
	} = useChatStoreActions();
	const ocgTwilioClientModule = useTwilioOcgClient();
	const isWidgetVisible = useWidgetVisibility();

	// Lazily restore the current conversation's Twilio state from the store.
	// Instead of eagerly restoring ALL conversations on page load (which
	// is O(n) × ~700ms per conversation), we only restore the one the user is
	// currently viewing. Other conversations are restored on-demand when the
	// user switches to them.
	const currentConvTwilioSid =
		(conversations[currentConversationId] as OmniEnhancedConversation | undefined)
			?.twilioConversationId ?? null;
	const currentConvHasStoredSid = !!currentConvTwilioSid;
	const currentConvAlreadyRestored = !!twilioConversationsWithMetadata[currentConversationId];

	useEffect(() => {
		const conversationRestoreFailed = failedRestoreConversations.has(currentConversationId);
		// BEARS-1934: once the Twilio bootstrap has fatally failed (any non-2xx
		// from `/access-token`), do not attempt to restore — every retry would
		// re-issue another POST and re-trigger the loop this fix is meant to
		// prevent.
		const bootstrapFailed = !!twilioBootstrapFatalError;
		if (
			!ocgTwilioClientModule ||
			!conversations[currentConversationId] ||
			!currentConvHasStoredSid ||
			currentConvAlreadyRestored ||
			conversationRestoreFailed ||
			bootstrapFailed
		) {
			return;
		}

		(async () => {
			try {
				await initializeTwilioConversation(currentConversationId, ocgTwilioClientModule);
			} catch (err) {
				failedRestoreConversations.add(currentConversationId);
				logException({
					exception: err,
					name: 'TwilioConversationRestoreFailed',
				});
			}
		})();
	}, [
		currentConversationId,
		currentConvHasStoredSid,
		currentConvAlreadyRestored,
		ocgTwilioClientModule,
		initializeTwilioConversation,
		conversations,
		ocgClient,
		twilioBootstrapFatalError,
	]);

	// -----------------------------------------------------------------------
	// Hydration: eagerly restore all persisted Twilio conversations that are
	// not yet in the in-memory store. This ensures conversations that existed
	// before the widget was opened get their Twilio Conversation objects
	// instantiated, enabling unread-count tracking across all conversations.
	//
	// TODO: Update this effect to use twilioConversationIds once they become
	// available in the items returned by the new omni-core list endpoint
	// '/api/v1/omni/conversation?helpCenterId={helpCenterId}'.
	// -----------------------------------------------------------------------
	const hydratedConversationsRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (!ocgTwilioClientModule) {
			return;
		}
		// BEARS-1934: skip eager hydration when the Twilio bootstrap has
		// already failed terminally — `hydrateTwilioConversation` calls into
		// `getOrCreateTwilioClient`, which would otherwise re-issue another
		// `/access-token` POST per render.
		if (twilioBootstrapFatalError) {
			return;
		}

		for (const [conversationId, conversation] of Object.entries(conversations)) {
			const conversationSid =
				(conversation as OmniEnhancedConversation)?.twilioConversationId ?? null;
			if (
				!conversationSid ||
				hydratedConversationsRef.current.has(conversationId) ||
				twilioConversationsWithMetadata[conversationId] ||
				conversationIsEnded(conversation)
			) {
				continue;
			}
			hydratedConversationsRef.current.add(conversationId);
			hydrateTwilioConversation({
				conversationId,
				conversationSid,
				ocgTwilioClientModule,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run when conversations or ocgTwilioClientModule change
	}, [ocgTwilioClientModule, conversations, twilioBootstrapFatalError]);

	// -----------------------------------------------------------------------
	// Global listener management: attach a `messageAdded` listener to every
	// non-ended Twilio conversation in the store, regardless of which
	// conversation the user is currently viewing. Listeners are tracked via
	// a ref Map and cleaned up when the conversation is removed or ends.
	// This effect only updates the unread messages count — message rendering
	// and typing indicators are handled by the per-current-conversation effect.
	// -----------------------------------------------------------------------
	const activeListenersRef = useRef<Map<string, () => void>>(new Map());
	const initialUnreadCheckedRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		const helpSeekerIdentity = twilioClientIdentity;
		const listenersMap = activeListenersRef.current;

		// Determine which conversations should have a listener:
		// conversations present in the metadata map that are NOT ended.
		const desiredConversationIds = new Set<string>();
		for (const [convId, metadata] of Object.entries(twilioConversationsWithMetadata)) {
			if (
				metadata?.conversation &&
				conversations[convId] &&
				!conversationIsEnded(conversations[convId])
			) {
				desiredConversationIds.add(convId);
			}
		}

		/**
		 * Computes and persists the number of unread messages for a conversation
		 * using the read-horizon index and the latest known message index.
		 */
		const updateUnreadCount = (
			conversationId: string,
			conversation: Conversation,
			latestMessageIndex: number,
		) => {
			const lastReadIndex = conversation.lastReadMessageIndex ?? -1;
			const unreadCount = Math.max(0, latestMessageIndex - lastReadIndex);

			updateTwilioUnreadMessagesCount({
				conversationId,
				unreadMessagesCount: unreadCount,
			});
		};

		// Attach listeners for newly added / not-yet-tracked conversations
		for (const convId of desiredConversationIds) {
			if (listenersMap.has(convId)) {
				continue; // already listening
			}

			const metadata = twilioConversationsWithMetadata[convId];
			const conversation = metadata.conversation;

			// One-time initial check: fetch recent messages from the Twilio
			// Conversation to find the latest agent message and compute the
			// unread count. Delegated to a store action so it reads the
			// latest state via `getState()` instead of a stale closure.
			if (!initialUnreadCheckedRef.current.has(convId)) {
				initialUnreadCheckedRef.current.add(convId);
				checkInitialUnreadMessages({
					conversationId: convId,
					conversation,
					helpSeekerIdentity,
				});
			}

			const onMessageAdded = (msg: Message) => {
				if (msg.author !== helpSeekerIdentity) {
					updateUnreadCount(convId, conversation, msg.index);
				}
			};

			// Listen for state changes on the Twilio conversation.
			// When the agent ends/closes the conversation, Twilio fires an
			// 'updated' event with 'state' in updateReasons and
			// conversation.state.current === 'closed'. We use this to
			// immediately transition the FE store to 'ENDED' state.
			const onConversationUpdated = ({
				conversation: updatedConversation,
				updateReasons,
			}: {
				conversation: Conversation;
				updateReasons: string[];
			}) => {
				if (updateReasons.includes('state') && updatedConversation.state?.current === 'closed') {
					markTwilioConversationEnded(convId);
				}
			};

			conversation.addListener('messageAdded', onMessageAdded);
			conversation.addListener('updated', onConversationUpdated);

			// Store a cleanup function that removes only these listeners
			listenersMap.set(convId, () => {
				conversation.removeListener('messageAdded', onMessageAdded);
				conversation.removeListener('updated', onConversationUpdated);
			});
		}

		// Remove listeners for conversations that have been removed or ended
		for (const [convId, cleanup] of listenersMap) {
			if (!desiredConversationIds.has(convId)) {
				cleanup();
				listenersMap.delete(convId);
			}
		}

		// Cleanup ALL listeners on unmount
		return () => {
			for (const [, cleanup] of listenersMap) {
				cleanup();
			}
			listenersMap.clear();
		};
	}, [
		twilioConversationsWithMetadata,
		conversations,
		twilioClientIdentity,
		updateTwilioUnreadMessagesCount,
		checkInitialUnreadMessages,
		markTwilioConversationEnded,
	]);

	const { conversation: twilioConversation } =
		twilioConversationsWithMetadata[currentConversationId] ?? {};

	// -----------------------------------------------------------------------
	// Per-current-conversation effect: handles UI-specific behaviour for the
	// conversation the user is actively viewing — typing indicators, backfill,
	// read-horizon, and live chat UI activation.
	// -----------------------------------------------------------------------
	useEffect(() => {
		const currentConversation = conversations[currentConversationId];
		let isUnmounted = false;

		// When the user switches to a different conversation, clear the previous
		// conversation's backfill flag so it re-runs on switch-back (to pick up
		// messages received while the listener was removed).
		// This must run before the early return below so that switching to a
		// non-Twilio conversation (e.g. a new AI chat) still clears the flag.
		if (lastBackfilledConversationId && lastBackfilledConversationId !== currentConversationId) {
			backfilledConversations.delete(lastBackfilledConversationId);
			lastBackfilledConversationId = currentConversationId;
		}

		// When the Twilio conversation has been processed (and deleted) by
		// omni-core, the SDK can no longer load it — `twilioConversation` will
		// be undefined and `initializeTwilioConversationImpl` will have hit the
		// "Not Found" path. We must still backfill historical messages for the
		// help-seeker via the BE endpoint, otherwise an ended conversation
		// reopened from history will appear empty (BEARS-1713).
		if (!twilioConversation) {
			const omniCurrentConversation = currentConversation as OmniEnhancedConversation | undefined;
			const interactionId = omniCurrentConversation?.interactionId ?? null;
			const omniProcessed = omniCurrentConversation?.omniProcessed ?? false;
			if (
				omniProcessed &&
				interactionId &&
				// Defer backfill until twilioClientIdentity is available so that
				// help-seeker messages can be correctly attributed (right-aligned).
				// twilioClientIdentity is derived from twilioClientTokenMeta which
				// is populated asynchronously by initializeTwilioConversationImpl;
				// this effect re-runs when it changes (it's in the dep array).
				twilioClientIdentity &&
				!backfilledConversations.has(currentConversationId)
			) {
				backfilledConversations.add(currentConversationId);
				lastBackfilledConversationId = currentConversationId;
				backfillMessagesForConversation(
					null,
					currentConversationId,
					twilioClientIdentity,
					backfillLiveChatMessages,
					interactionId,
					omniProcessed,
					ocgClient,
				);
			}
			return;
		}

		const helpSeekerIdentity = twilioClientIdentity;
		const conversationSid = twilioConversation.sid;

		const onMessageAdded = (msg: Message) => {
			if (msg.author) {
				removeTwilioAgentTyping({ identity: msg.author });
			}
			// WhatsApp help-seeker: no optimistic copy exists (they chat from
			// their phone), so store the realtime echo as a StoreHuman item.
			if (isWhatsAppAuthor(msg.author) && fg('csm-omni-whatsapp-support')) {
				addTwilioLiveChatHumanMessage(getTwilioLiveChatMessage(msg, conversationSid));
				return;
			}
			if (msg.author === helpSeekerIdentity) {
				// Default: do not add own messages to the chat store, because
				// they are already added optimistically by sendMessage when the
				// help seeker hits Enter/Send.
				//
				// Exception (CSM Twilio attachments): when the message carries
				// native Twilio media attachments, the optimistic
				// `liveChatMessageOnly: true` path is intentionally skipped
				// (see `useHandleChatInputSubmit`'s csm-files branch), so we
				// must add the bubble here from the realtime echo.
				//
				// Belt-and-braces dedupe: if a future caller adds an
				// optimistic copy AND we then receive the realtime echo of
				// the same send, drop the echo by matching on the
				// `csmClientMessageId` attribute the send-provider stamps
				// onto every attachment-bearing send. We track recently
				// stamped ids in `recentClientMessageIds` (module ref).
				const ownMsgHasAttachments = !!msg.attachedMedia && msg.attachedMedia.length > 0;
				if (!ownMsgHasAttachments || !fg('csm-omni-channel-live-chat-attachments')) {
					return;
				}
				const attrs = (msg.attributes ?? null) as { csmClientMessageId?: unknown } | null;
				const echoId =
					attrs && typeof attrs.csmClientMessageId === 'string' ? attrs.csmClientMessageId : null;
				if (echoId && consumeRecentCsmClientMessageId(echoId)) {
					// We already added this message via the optimistic path;
					// drop the realtime echo so the bubble doesn't double up.
					return;
				}
				// Help-seeker's own attachment-bearing message: store as
				// StoreHuman (right side, no agent avatar/box). Uses
				// addTwilioLiveChatHumanMessage which has no dedup logic, so
				// the message is always appended immediately.
				addTwilioLiveChatHumanMessage(getTwilioLiveChatMessage(msg, conversationSid));
				return;
			}
			// Agent or system message: store as TWILIO_LIVE_CHAT_AGENT (left side).
			addTwilioLiveChatMessage(getTwilioLiveChatMessage(msg, conversationSid));
		};

		// Listen for agent participants joining the Twilio conversation.
		// This fires when a human agent accepts the chat task, providing
		// reliable agent-join detection to dismiss the transient "connecting" system message.
		const onParticipantJoined = (participant: Participant) => {
			if (isParticipantAnAgent(participant, helpSeekerIdentity)) {
				setTwilioAgentJoined({ conversationId: currentConversationId });
			}
		};

		const onTypingStarted = (participant: Participant) => {
			if (isParticipantAnAgent(participant, helpSeekerIdentity)) {
				addTwilioAgentTyping({ identity: participant.identity! });
			}
		};

		const onTypingEnded = (participant: Participant) => {
			if (isParticipantAnAgent(participant, helpSeekerIdentity)) {
				removeTwilioAgentTyping({ identity: participant.identity! });
			}
		};

		// For active conversations, attach a real-time message listener and activate live chat UI.
		// For ended conversations, skip the listener (no new messages expected).
		// NOTE: We must NOT gate this on `liveChatMetadata`. That field is only
		// populated when the user goes through the in-session AI → human handoff
		// flow, so gating on it would prevent the listener from being attached
		// when a returning user reopens a pre-existing escalated conversation
		// from chat history — silently dropping any agent messages received
		// in real time after the user opens the conversation (BEARS-1740).
		// We DO check the Twilio conversation's own `state.current` so that
		// conversations the agent has already closed (but the FE store still
		// considers ESCALATED) do not get treated as live.
		const twilioStateClosed = twilioConversation.state?.current === 'closed';
		if (!conversationIsEnded(currentConversation) && !twilioStateClosed) {
			setCsmLiveChatSubscriptionId(conversationSid);

			if (!isUnmounted) {
				twilioConversation.addListener('messageAdded', onMessageAdded);
				twilioConversation.addListener('participantJoined', onParticipantJoined);
				twilioConversation.addListener('typingStarted', onTypingStarted);
				twilioConversation.addListener('typingEnded', onTypingEnded);

				// After attaching the listener, check if an agent already joined before
				// the listener was registered. Twilio SDK events are fire-and-forget,
				// so if 'participantJoined' fired before addListener was called, the
				// event is silently lost. This getParticipants() call closes that race.
				twilioConversation
					.getParticipants()
					.then((participants) => {
						if (isUnmounted) {
							return;
						}
						if (
							participants.some((participant) =>
								isParticipantAnAgent(participant, helpSeekerIdentity),
							)
						) {
							setTwilioAgentJoined({ conversationId: currentConversationId });
						}
					})
					.catch((error) => {
						logException({
							exception: error,
							name: 'TwilioGetParticipantsFailed',
						});
					});
			}
		}

		// Backfill historical messages for both active and ended conversations
		if (!backfilledConversations.has(currentConversationId)) {
			backfilledConversations.add(currentConversationId);
			lastBackfilledConversationId = currentConversationId;
			const omniCurrentConversation = conversations[currentConversationId] as
				| OmniEnhancedConversation
				| undefined;
			backfillMessagesForConversation(
				twilioConversation,
				currentConversationId,
				helpSeekerIdentity,
				backfillLiveChatMessages,
				omniCurrentConversation?.interactionId ?? null,
				omniCurrentConversation?.omniProcessed ?? false,
				ocgClient,
			);
		}

		return () => {
			isUnmounted = true;
			// Remove only the listeners we attached in this effect, leaving the
			// global effect's messageAdded listener intact.
			twilioConversation.removeListener('messageAdded', onMessageAdded);
			twilioConversation.removeListener('participantJoined', onParticipantJoined);
			twilioConversation.removeListener('typingStarted', onTypingStarted);
			twilioConversation.removeListener('typingEnded', onTypingEnded);
			// Clear all typing indicators when unmounting or switching conversations
			clearTwilioAgentsTyping();
		};
	}, [
		currentConversationId,
		twilioClientIdentity,
		twilioConversation,
		conversations,
		addTwilioLiveChatHumanMessage,
		addTwilioLiveChatMessage,
		backfillLiveChatMessages,
		setCsmLiveChatSubscriptionId,
		addTwilioAgentTyping,
		removeTwilioAgentTyping,
		clearTwilioAgentsTyping,
		setTwilioAgentJoined,
		updateTwilioUnreadMessagesCount,
		ocgClient,
	]);

	// -----------------------------------------------------------------------
	// Read-horizon effect: marks all messages as read for the current
	// conversation when the widget is visible. Extracted into its own effect
	// so that visibility changes don't tear down and re-attach the
	// heavyweight listeners in the per-current-conversation effect above.
	// Also re-runs when the current conversation's unread count changes
	// (e.g. an agent message arrives while the user is viewing the chat)
	// so that the count is immediately cleared.
	// -----------------------------------------------------------------------
	const currentConversationUnreadMessagesCount =
		twilioConversationsWithMetadata[currentConversationId]?.unreadMessagesCount ?? 0;

	useEffect(() => {
		if (
			!isWidgetVisible ||
			!twilioConversation ||
			!currentConversationId ||
			currentConversationUnreadMessagesCount < 1
		) {
			return;
		}

		twilioConversation
			.setAllMessagesRead()
			.then(() => {
				updateTwilioUnreadMessagesCount({
					conversationId: currentConversationId,
					unreadMessagesCount: 0,
				});
			})
			.catch((error) => {
				logException({
					exception: error,
					name: 'TwilioConversationSetAllMessagesReadFailed',
				});
			});
	}, [
		isWidgetVisible,
		twilioConversation,
		currentConversationId,
		currentConversationUnreadMessagesCount,
		updateTwilioUnreadMessagesCount,
	]);

	// -----------------------------------------------------------------------
	// Aggregation effect: watches unreadMessagesCount across ALL Twilio
	// conversations in the store and notifies the host via the
	// onHasUnreadMessagesChanged callback whenever the aggregate changes.
	// Only fires when the boolean value actually changes to avoid redundant calls.
	// -----------------------------------------------------------------------
	const hasUnreadMessages = useHasUnreadMessages();
	const prevHasUnreadRef = useRef<boolean | null>(null);

	useEffect(() => {
		if (!onHasUnreadMessagesChanged) {
			return;
		}

		if (prevHasUnreadRef.current !== hasUnreadMessages) {
			prevHasUnreadRef.current = hasUnreadMessages;
			onHasUnreadMessagesChanged(hasUnreadMessages);
		}
	}, [hasUnreadMessages, onHasUnreadMessagesChanged]);

	return null;
};

const getTwilioLiveChatMessage = (msg: Message, conversationSid: string): TwilioLiveChatMessage => {
	const attachments = fg('csm-omni-channel-live-chat-attachments')
		? mapTwilioAttachedMedia(msg)
		: undefined;
	return {
		sid: msg.sid,
		content: msg.body ?? '',
		author: msg.author,
		dateCreated: msg.dateCreated,
		conversationSid,
		attributes: msg.attributes as TwilioLiveChatMessage['attributes'] | undefined,
		...(attachments && attachments.length > 0 ? { attachments } : {}),
	};
};

/**
 * Maps the Twilio SDK's `Message.attachedMedia` array to the store's
 * `CsmTwilioAttachment` shape. We do NOT eagerly resolve temporary
 * download URLs here — Twilio's MCS URLs expire after a few minutes,
 * so the renderer re-resolves them on demand at display/click time.
 *
 * Returns `undefined` when the message has no media; callers can spread
 * the result without producing an empty `attachments: []` field.
 */
const mapTwilioAttachedMedia = (msg: Message) => {
	const attached = msg.attachedMedia;
	if (!attached || attached.length === 0) {
		return undefined;
	}
	return attached.map((media) => ({
		mediaSid: media.sid,
		contentType: media.contentType,
		filename: media.filename,
		size: media.size,
	}));
};

/**
 * Fetches historical messages and backfills them into the chat store.
 * This handles the case where the user refreshes the page or navigates away
 * and back — the realtime listener only catches new messages, so we need to
 * replay the history.
 *
 * When `omniProcessed` is true, messages are fetched from the BE endpoint
 * GET /interactions/{interactionId}/messages instead of the SDK.
 *
 * All messages are backfilled — the {@link backfillLiveChatMessages} action uses
 * `helpSeekerIdentity` to categorize messages: help-seeker messages are added as
 * `StoreHuman` items (right-aligned) and agent messages as `TWILIO_LIVE_CHAT_AGENT`
 * (`StoreTwilioLiveChat`) items (left-aligned). Deduplication by message SID
 * prevents duplicates.
 */
const backfillMessagesForConversation = async (
	conversation: Conversation | null,
	conversationId: string,
	helpSeekerIdentity: string | null,
	backfillLiveChatMessages: (params: {
		messagesToBackfill: TwilioLiveChatMessage[];
		helpSeekerIdentity: string | null;
		conversationId: string;
	}) => void,
	interactionId: string | null,
	omniProcessed: boolean,
	ocgClient: OcgClient,
): Promise<void> => {
	try {
		let liveChatMessages: TwilioLiveChatMessage[];

		if (omniProcessed && interactionId) {
			// Twilio conversation has been deleted — fetch messages from the BE endpoint.
			// `conversation` may be null in this branch (the SDK can no longer load it).
			// Auth header lookup and 401 retry are centralised inside `ocgClient`
			// (BEARS-1804), so non-cookie-auth contexts (BEARS-1749) are handled
			// transparently.
			liveChatMessages = await ocgClient.getConversationMessages(interactionId);
		} else {
			if (!conversation) {
				// No Twilio SDK conversation and not omni-processed — nothing we can do.
				return;
			}
			const twilioMessages = await fetchLiveChatMessageHistory(conversation);
			liveChatMessages = twilioMessages.map((msg) =>
				getTwilioLiveChatMessage(msg, conversation.sid),
			);
		}

		if (liveChatMessages.length === 0) {
			return;
		}

		backfillLiveChatMessages({
			messagesToBackfill: liveChatMessages,
			helpSeekerIdentity,
			conversationId,
		});
	} catch (error) {
		logException({
			exception: error,
			name: 'TwilioMessageBackfillFailed',
		});
	}
};
